import "@/client/libs/dayjs";

import { i18n } from "@lingui/core";
import { detect, fromStorage, fromUrl } from "@lingui/detect-locale";
import { I18nProvider } from "@lingui/react";
import { languages } from "@stylingcv/utils";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { defaultLocale, dynamicActivate } from "../libs/lingui";
import { updateUser } from "../services/user";
import { useAuthStore } from "../stores/auth";
import { getCurrentUrlLocale } from "../utils/url-locales";
import { trackLanguageChange } from "../utils/gtm";

type Props = {
  children: React.ReactNode;
};

export const LocaleProvider = ({ children }: Props) => {
  const userLocale = useAuthStore((state) => state.user?.locale ?? defaultLocale);
  const location = useLocation(); // Listen to route changes

  useEffect(() => {
    // Enhanced detection: URL path takes priority, then query param, then storage, then user preference
    const detectedLocale = getCurrentUrlLocale();

    // Activate the locale only if it's supported
    const isSupported = languages.some(
      (lang) =>
        lang.locale.toLowerCase() === detectedLocale.toLowerCase() ||
        lang.id.toLowerCase() === detectedLocale.toLowerCase(),
    );

    if (isSupported) {
      void dynamicActivate(detectedLocale);
    } else {
      void dynamicActivate(defaultLocale);
    }
  }, [userLocale, location.pathname]); // Re-run when URL path changes

  return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
};

export const changeLanguage = async (locale: string) => {
  // Track language change before navigation
  const previousLocale = window.localStorage.getItem("locale") || getCurrentUrlLocale() || defaultLocale;
  if (previousLocale !== locale) {
    trackLanguageChange({ fromLanguage: previousLocale, toLanguage: locale });
  }

  // Update locale in local storage
  window.localStorage.setItem("locale", locale);

  // Import URL utilities dynamically to avoid dependency issues
  const { navigateToLocale } = await import("../utils/url-locales");

  // Update locale in user profile, if authenticated
  const state = useAuthStore.getState();
  if (state.user) {
    await updateUser({ locale }).catch((error) => {
      console.error("[Language] Failed to update user locale:", error);
      return null;
    });
  }

  // Navigate to new locale URL (will trigger locale detection)
  navigateToLocale(locale);
};
